import { Controller } from '@hotwired/stimulus'
import { format as formatTz, utcToZonedTime } from 'date-fns-tz'
import parseISO from 'date-fns/parseISO'
import { trans, WEBHOOKS_SUBSCRIPTION_COUNT, WEBHOOKS_AT_CAP_TOOLTIP, WEBHOOKS_ACTION_ROTATE_SECRET, WEBHOOKS_ACTION_SEND_TEST_EVENT, WEBHOOKS_STATUS_AUTO_DISABLED, WEBHOOKS_ACTION_VIEW_LOGS, EDIT, ENABLE, DISABLE, DELETE, ACTIVE, DISABLED } from '../../translator'
import escapeHtml from '../../utils/sanitize'
import { getTimestampFormat } from '../../utils/utils'

declare const accountDateFormat: string
declare const accountTimezone: string
declare const accountIsUseAmPm: string
declare const selectedTimezone: string

interface WebhookSubscription {
  id: string
  url: string
  event_types: string[]
  event_filters?: Record<string, Record<string, Array<{ kind: string, id: string }>>>
  is_active: boolean
  is_auto_disabled?: boolean
  created_at: string
}

interface WebhookChangedDetail {
  action: 'created' | 'updated' | 'statusChanged' | 'deleted'
  subscription?: WebhookSubscription
  id?: string
}

export default class extends Controller {
  static targets = ['table', 'count', 'newButton', 'emptyState']

  static values = {
    cap: Number,
    logsUrlTemplate: String
  }

  declare capValue: number
  declare logsUrlTemplateValue: string
  declare readonly tableTarget: HTMLElement
  declare readonly countTarget: HTMLElement
  declare readonly newButtonTarget: HTMLButtonElement
  declare readonly emptyStateTarget: HTMLElement

  count = 0

  connect (): void {
    this.count = this.tableTarget.querySelectorAll('tr[data-webhook-id]').length
  }

  onWebhookChanged (event: CustomEvent<WebhookChangedDetail>): void {
    const { action, subscription, id } = event.detail

    if (action === 'created' && subscription) {
      this.emptyStateTarget.insertAdjacentHTML('afterend', this.buildRowHtml(subscription))
      this.count += 1
    } else if ((action === 'updated' || action === 'statusChanged') && subscription) {
      this.replaceRow(subscription)
    } else if (action === 'deleted' && id) {
      this.removeRow(id)
      this.count -= 1
    }

    this.updateCount()
  }

  private findRow (id: string): HTMLElement | null {
    return this.tableTarget.querySelector(`tr[data-webhook-id="${id}"]`)
  }

  private replaceRow (subscription: WebhookSubscription): void {
    const row = this.findRow(subscription.id)
    if (row) {
      row.outerHTML = this.buildRowHtml(subscription)
    }
  }

  private removeRow (id: string): void {
    const row = this.findRow(id)
    if (row) {
      row.remove()
    }
  }

  private updateCount (): void {
    this.countTarget.textContent = trans(WEBHOOKS_SUBSCRIPTION_COUNT, { '%count%': this.count, '%max%': this.capValue }, 'integrations')
    const atCap = this.count >= this.capValue
    this.newButtonTarget.disabled = atCap
    if (atCap) {
      this.newButtonTarget.title = trans(WEBHOOKS_AT_CAP_TOOLTIP, {}, 'integrations')
    } else {
      this.newButtonTarget.removeAttribute('title')
    }
    this.emptyStateTarget.classList.toggle('d-none', this.count > 0)
  }

  private formatIn (date: Date, timezone: string, withTimezone: boolean): string {
    const dateFormat = accountDateFormat.replace('mm', 'MM')
    return formatTz(
      utcToZonedTime(date, timezone),
      `${dateFormat} ${getTimestampFormat(accountIsUseAmPm, withTimezone)}`,
      { timeZone: timezone }
    )
  }

  private formatDate (isoString: string): string {
    const date = parseISO(isoString)
    const timezonesDiffer = selectedTimezone !== accountTimezone &&
      this.formatIn(date, accountTimezone, false) !== this.formatIn(date, selectedTimezone, false)

    if (!timezonesDiffer) {
      return this.formatIn(date, accountTimezone, false)
    }

    return `${this.formatIn(date, accountTimezone, true)}<br><small class="text-muted">${this.formatIn(date, selectedTimezone, true)}</small>`
  }

  // The route is built in Twig and passed in as a template so the path stays in one place.
  private logsUrl (id: string): string {
    return this.logsUrlTemplateValue.replace('__ID__', encodeURIComponent(id))
  }
  
  // Mirrors integrations/components/webhook-subscription-row.html.twig -- a subscription the
  // failure threshold disabled reads differently from one an admin disabled by hand.
  private buildStatusBadge (subscription: WebhookSubscription): string {
    if (subscription.is_active) {
      return `<span class="badge badge-success">${trans(ACTIVE, {}, 'states')}</span>`
    }

    if (subscription.is_auto_disabled === true) {
      return `<span class="badge badge-danger">${trans(WEBHOOKS_STATUS_AUTO_DISABLED, {}, 'integrations')}</span>`
    }

    return `<span class="badge badge-light">${trans(DISABLED, {}, 'states')}</span>`
  }

  private buildRowHtml (subscription: WebhookSubscription): string {
    const url = escapeHtml(subscription.url)
    const eventTypes = subscription.event_types.join(',')
    const eventTypesParam = JSON.stringify(subscription.event_types).replace(/"/g, '&quot;')
    const eventFiltersParam = JSON.stringify(subscription.event_filters ?? {}).replace(/"/g, '&quot;')
    const statusBadge = this.buildStatusBadge(subscription)
    const toggleAction = subscription.is_active ? 'disable' : 'enable'
    const toggleLabel = subscription.is_active ? trans(DISABLE, {}, 'actions') : trans(ENABLE, {}, 'actions')

    return `
      <tr data-webhook-id="${subscription.id}" data-webhook-url="${url}" data-webhook-event-types="${eventTypes}">
        <td class="priority-1">
          <div class="media align-items-center">
            <div class="media-body">
              <h5 class="item-heading mb-0 rtext-h-sm text-truncate" style="max-width: 320px;" title="${url}">${url}</h5>
            </div>
          </div>
        </td>
        <td class="priority-2">
          <div class="media align-items-center">
            <div class="media-body">
              <span class="name mb-0 item-info rtext-sm">${subscription.event_types.join(', ')}</span>
            </div>
          </div>
        </td>
        <td class="priority-1 js-webhook-status-cell">
          <div class="media align-items-center">
            <div class="media-body">${statusBadge}</div>
          </div>
        </td>
        <td class="priority-3">
          <div class="media align-items-center">
            <div class="media-body">
              <span class="name mb-0 item-info rtext-sm">${this.formatDate(subscription.created_at)}</span>
            </div>
          </div>
        </td>
        <td class="text-right table-no-follow-link">
          <div class="dropdown">
            <a class="btn btn-sm btn-icon-only text-light" href="#" role="button" data-toggle="dropdown" data-boundary="window" aria-haspopup="true" aria-expanded="false">
              <i class="fas fa-ellipsis-v"></i>
            </a>
            <div class="dropdown-menu dropdown-menu-right dropdown-menu-arrow">
              <span class="dropdown-item" data-action="click->save-webhook-modal#openEditModal" data-save-webhook-modal-id-param="${subscription.id}" data-save-webhook-modal-url-param="${url}" data-save-webhook-modal-event-types-param="${eventTypesParam}" data-save-webhook-modal-event-filters-param="${eventFiltersParam}">${trans(EDIT, {}, 'actions')}</span>
              <span class="dropdown-item" data-action="click->save-webhook-modal#openRotateModal" data-save-webhook-modal-id-param="${subscription.id}">${trans(WEBHOOKS_ACTION_ROTATE_SECRET, {}, 'integrations')}</span>
              <span class="dropdown-item" data-action="click->test-event#send" data-test-event-id-param="${subscription.id}" data-test-event-url-param="${url}">${trans(WEBHOOKS_ACTION_SEND_TEST_EVENT, {}, 'integrations')}</span>
              <a class="dropdown-item" href="${escapeHtml(this.logsUrl(subscription.id))}">${trans(WEBHOOKS_ACTION_VIEW_LOGS, {}, 'integrations')}</a>
              <span class="dropdown-item" data-action="click->save-webhook-modal#${toggleAction}" data-save-webhook-modal-id-param="${subscription.id}">${toggleLabel}</span>
              <span class="dropdown-item" data-action="click->save-webhook-modal#openDeleteModal" data-save-webhook-modal-id-param="${subscription.id}">${trans(DELETE, {}, 'actions')}</span>
            </div>
          </div>
        </td>
      </tr>
    `
  }
}
